import { useMemo } from "react";
import {
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
  FunctionField,
  TabbedShowLayout,
  Tab,
  ReferenceField,
  type RaRecord,
} from "react-admin";
import {
  Typography,
  Divider,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { formatDisplayDateTime } from "@/utils/date-format";
import { RecipientsList, StatusChip } from "./components";
import {
  describeBroadcastParameters,
  describeBroadcastRecipientSource,
  normalizeRecipientIdsToItsIds,
} from "./components/broadcast-summary";
import { getBroadcastStatusPollIntervalMs } from "./components/broadcast-polling";

const FilterCriteriaSummary = () => {
  const record = useRecordContext();
  const { recipientSource, filterStats } = useMemo(
    () =>
      describeBroadcastRecipientSource({
        broadcastKind: record?.kind as string | undefined,
        filterCriteria: record?.filterCriteria,
        recipientItsIds: normalizeRecipientIdsToItsIds(record?.recipientIds),
      }),
    [record?.kind, record?.filterCriteria, record?.recipientIds]
  );

  return (
    <>
      <Chip label={`Source: ${recipientSource}`} color="primary" variant="outlined" size="small" />
      {filterStats && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {filterStats.label ??
            `${filterStats.rulesCount} rule(s), ${filterStats.groupsCount} group(s)`}
        </Typography>
      )}
    </>
  );
};

const ParametersSummary = () => {
  const record = useRecordContext();
  const rows = useMemo(() => describeBroadcastParameters(record?.parameters), [record?.parameters]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No template parameters
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell sx={{ fontWeight: 500, width: 80 }}>{`{{${row.key}}}`}</TableCell>
              <TableCell>{row.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default function WhatsappBroadcastShow() {
  const record = useRecordContext();

  const errorSection = useMemo(() => {
    if (!record?.errorMessage) return null;
    return (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom color="error">
          Error
        </Typography>
        <TextField source="errorMessage" label="Error Message" />
      </>
    );
  }, [record?.errorMessage]);

  return (
    <Show
      queryOptions={{
        refetchInterval: (query) =>
          getBroadcastStatusPollIntervalMs(query.state.data?.status as string | undefined),
      }}
    >
      <TabbedShowLayout>
        <Tab label="Details">
          <SimpleShowLayout>
            <TextField source="name" label="Broadcast Name" />
            <TextField source="kind" label="Broadcast type" emptyText="TEMPLATE" />
            <ReferenceField
              source="eventId"
              reference="events"
              label="Event (RSVP)"
              link="show"
              emptyText="—"
            >
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField
              source="miqaatId"
              reference="miqaats"
              label="Miqaat (RSVP)"
              link="show"
              emptyText="—"
            >
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField
              source="templateName"
              reference="whatsappTemplates"
              label="Template"
              link="show"
            >
              <TextField source="name" />
            </ReferenceField>
            <FunctionField
              label="Status"
              render={(r) => <StatusChip status={r.status as string} />}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Timing
            </Typography>
            <FunctionField
              label="Created"
              render={(r: RaRecord) => formatDisplayDateTime(r?.createdAt, { empty: "—" })}
            />
            <FunctionField
              label="Started"
              render={(r: RaRecord) => formatDisplayDateTime(r?.startedAt, { empty: "—" })}
            />
            <FunctionField
              label="Completed"
              render={(r: RaRecord) => formatDisplayDateTime(r?.completedAt, { empty: "—" })}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Filter Criteria
            </Typography>
            <FilterCriteriaSummary />
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Parameters
            </Typography>
            <ParametersSummary />
            {errorSection}
          </SimpleShowLayout>
        </Tab>
        <Tab label="Recipients" path="recipients">
          <RecipientsList />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
}
